// dependencies
import { List, Map } from "immutable";
import { useEffect, useEffectEvent, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { DataRouteMatch, matchRoutes, useLocation, useMatches, useParams, useSearchParams } from "react-router-dom";

// components
import router from "./router";

//utilities
import Reload from "../../utils/Reload";
import {
	ContentVisibility,
	ROUTER_PAGE_TYPES,
	ROUTER_PAGE_TYPE_STORAGE_KEY,
	type RouterPageType,
	deriveTransitionTypeFromPageTypes,
	detectPageTypeTransition,
	parseQueryString,
	selectMatchedRouteName,
} from "./utils";

// Constants
import RouterTags from "../../constants/RouterTags";

const hasTemplateTag = (route: DataRouteMatch | null | undefined): boolean => {
	const tags = route?.route?.handle?.tags ?? [];
	return Boolean(tags.includes(RouterTags.USES_TEMPLATE) || tags.includes(RouterTags.USES_TEMPLATE_AND_REACT));
};

const getRoutePageType = (route: DataRouteMatch | null): RouterPageType | null => {
	if (!route) {
		return null;
	}

	const isTemplate = hasTemplateTag(route);

	return isTemplate ? ROUTER_PAGE_TYPES.template : ROUTER_PAGE_TYPES.react;
};

const readPersistedPageType = (): RouterPageType | null => {
	try {
		const storedValue = sessionStorage.getItem(ROUTER_PAGE_TYPE_STORAGE_KEY);

		if (storedValue === ROUTER_PAGE_TYPES.react || storedValue === ROUTER_PAGE_TYPES.template) {
			return storedValue;
		}

		return null;
	} catch {
		return null;
	}
};

const persistPageType = (type: RouterPageType): void => {
	try {
		sessionStorage.setItem(ROUTER_PAGE_TYPE_STORAGE_KEY, type);
	} catch {
		// swallowing storage errors prevents crashes in private browsing
	}
};

const clearPersistedPageType = (): void => {
	try {
		sessionStorage.removeItem(ROUTER_PAGE_TYPE_STORAGE_KEY);
	} catch {
		// noop
	}
};

/**
 * Gets props about the route, mostly used for backwards compatibility
 */
export const useCurrentRouteProps = () => {
	const currentRoute = useCurrentRoute();

	// get the name of the page. This is captured from the path and translated
	// to a name. This is mostly used for backwards compatibility in the redux
	// store. This value can be 'legacyPageName' prop in the handle property
	// of the route.
	const pageName = useMemo(() => {
		if (currentRoute) {
			return selectMatchedRouteName(currentRoute);
		} else {
			return null;
		}
	}, [currentRoute]);

	// get the active navigation
	const activeNavigationItem: string | null = currentRoute?.route?.handle?.activeNavigation ?? null;

	return {
		pageName,
		activeNavigationItem,
	};
};

/**
 * A hook used to determine if the current route is defined in the router.
 * Since not all pages are defined in the router, this is used to determine
 * if the current page is a legacy page or not.
 */
export const useCurrentRoute = () => {
	const location = useLocation();

	const currentRoute = useMemo(() => {
		const matches = matchRoutes(router.routes, window.location);
		const routeMatch = matches?.[matches.length - 1];

		return routeMatch;
	}, [location]);

	return currentRoute;
};

/**
 * This hook enforces the reloading of pages when transitioning between Tornado Template pages
 * and React SPA pages. We don't expect SPA loaded pages to reload when coming from another
 * SPA loaded page.
 *
 * Reloads are needed in both directions:
 * - React page -> Template page: Template pages need full page refresh to load new content
 * - Template page -> React page: The main navbars (user info + company navbars) don't populate
 *   until a full page change. We need to hide React content and refresh to ensure navbars are
 *   properly initialized. Once we rewrite the main navbars to be in React, we can remove the
 *   reload when going from tornado template -> react page.
 *
 * Returns a loading state that indicates when we're transitioning between page types.
 */
export const useRouterPageRefresher = (): boolean => {
	const currentRoute = useCurrentRoute();
	const previousRouteRef = useRef<DataRouteMatch | null>(null);
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		const previousRoute = previousRouteRef.current;
		const persistedPageType = readPersistedPageType();
		const currentPageType = getRoutePageType(currentRoute ?? null);
		let transitionType = detectPageTypeTransition(previousRoute ?? null, currentRoute ?? null);

		if (transitionType === "INITIAL_MOUNT") {
			const derivedTransitionType = deriveTransitionTypeFromPageTypes(persistedPageType, currentPageType);

			if (derivedTransitionType) {
				transitionType = derivedTransitionType;
				clearPersistedPageType();
			}
		} else if (persistedPageType) {
			clearPersistedPageType();
		}

		if (transitionType === "REACT_TO_TEMPLATE") {
			persistPageType(ROUTER_PAGE_TYPES.template);
		}

		if (transitionType === "REACT_TO_TEMPLATE" || transitionType === "TEMPLATE_TO_REACT") {
			setIsLoading(true);
			ContentVisibility.hideAll();
			Reload();
			return;
		}

		previousRouteRef.current = currentRoute ?? null;
	}, [currentRoute]);

	// Use useEffectEvent to access latest values without causing re-renders
	const handleVisibilityChange = useEffectEvent(() => {
		// is a template page if the route has the USES_TEMPLATE tag OR if the route is the catch-all (not found in router)
		const isTemplatePage = hasTemplateTag(currentRoute);
		const isTemplateAndReact = Boolean(
			currentRoute?.route?.handle?.tags?.includes(RouterTags.USES_TEMPLATE_AND_REACT)
		);
		const isCatchAllRoute = currentRoute?.route?.path === "*";
		const isInReactRouterList = currentRoute !== null && !isCatchAllRoute;

		if (isLoading) {
			ContentVisibility.hideAll();
		} else if (isCatchAllRoute) {
			ContentVisibility.showAll();
		} else if (isTemplateAndReact) {
			ContentVisibility.showAll();
		} else if (isTemplatePage || !isInReactRouterList) {
			ContentVisibility.showOnlyTornado();
		} else {
			ContentVisibility.showOnlyReact();
		}
	});

	// Only run visibility logic when isLoading changes
	// handleVisibilityChange is stable (from useEffectEvent) and doesn't need to be in deps
	useEffect(() => {
		handleVisibilityChange();
	}, [isLoading, currentRoute]);

	return isLoading;
};

/**
 * This is a compatibility hook for the legacy redux store. It will sync the
 * current route data to the redux store. This is used by many pages that
 * rely on route data to exist in the redux store.
 * Returns a loading state indicating when transitioning to a template page.
 */
export const useReactRouterCompat = (): boolean => {
	const params = useParams();
	const matches = useMatches();
	const location = useLocation();
	const dispatch = useDispatch<any>();
	const [currentQueryParameters] = useSearchParams();
	const { pageName, activeNavigationItem } = useCurrentRouteProps();
	const isLoading = useRouterPageRefresher();

	const searchString = currentQueryParameters.toString();

	/**
	 * Updates the redux store with the current route information. Ideally we
	 * don't rely on the redux store for this, but to reduce the scope of the
	 * transiton to react router, this will work as a backwards compatible
	 * solution.
	 */
	const updateReduxStore = () => {
		// get the default values of the matched routes
		const defaultValues = matches
			.map((match) => (match.handle as any)?.defaultValues ?? {})
			.reduce((acc, cur) => ({ ...acc, ...cur }), {});

		// sets the current active navigation item
		const activeNavigation = undefined;
		// values are just the params. For the case where there is a
		// 'defaultValues' prop, we add those to the params
		const values = Map({
			...params,
			...defaultValues,
		});
		// keys are the keys of the params
		const keys = List(Object.keys(params));
		// name of the page, derived from the path
		const name = pageName;
		// page is the component that is rendered, I don't think we need this
		const page = null;
		// query is the parsed query string
		const query = Map(parseQueryString(searchString));
		// querystring is the raw query string
		const queryString = searchString;
		// tags are usually communcating to other components. For example
		// the "userpage" tag informs the feedback component to automatically
		// set the user as the subject
		const tags = List(matches.map((match) => (match.handle as any)?.tags ?? []).flat(1));

		const payloadProps = {
			keys,
			name,
			page,
			tags,
			query,
			values,
			queryString,
			activeNavigation,
		};

		dispatch({
			type: "ROUTER_LOCATION_CHANGED",
			payload: {
				props: payloadProps,
				location,
			},
		});
	};

	/**
	 * Updates the active navigation item in the sub navigation container.
	 * This can be removed once the sub navigation is moved to react. This
	 * replaces the <Navigation> container from the old routing system.
	 */
	const updateActiveNavigation = () => {
		const subHeader = document.getElementById("sub-nav-header");

		if (subHeader) {
			const existingCaret = document.getElementById("sub-nav-header")?.querySelector(".arrow");

			if (existingCaret) {
				existingCaret.remove();
			}

			// find the nav item to make active
			const navItem = subHeader.querySelector(`[data-nav-item="${activeNavigationItem}"]`);

			if (navItem) {
				// create a new caret and add it to the active navigation item.
				const newCaretElement = document.createElement("div");
				newCaretElement.setAttribute("class", "arrow");
				navItem.querySelector("a")?.appendChild(newCaretElement);
				// add selected class to bold active nav text
				navItem.querySelector("a")?.setAttribute("class", "item selected");
			}
		}
	};

	useEffect(() => {
		updateReduxStore();
		updateActiveNavigation();
	}, [location, searchString]);

	return isLoading;
};
